/**
 * The thing that actually reports a call: takes readings, buffers them, and
 * hands them over in batches.
 *
 * Deliberately owns no clock and no fetch — both are injected. The rules worth
 * getting right here are about *when* things are sent and what happens when
 * sending fails, and neither is testable through a real timer and a real
 * network without the test becoming about the timer and the network.
 *
 * Why batches at all: a call takes a reading every few seconds from both sides,
 * and one request per reading would be a POST every 5 seconds per person for
 * the length of every call — on a phone, on mobile data, while that same phone
 * is carrying the call. So readings accumulate and go in groups, and the last
 * group is sent with `keepalive` because the page is often being torn down at
 * exactly that moment (see api/client.ts).
 *
 * A dropped batch is a hole in a chart, never a broken call: every failure here
 * is swallowed, and a batch that could not be sent waits for the next flush.
 */

import type { CallSample } from './callStats.ts';
import { sampleFrom, type StatsCursor } from './callStats.ts';
import type { StatEntry } from './callDiagnostics.ts';

/** A moment worth a line: it connected, ICE restarted, a camera went off. */
export interface CallEventWire {
  at: number;
  kind: string;
  detail: string;
}

export interface TelemetryBatch {
  samples: CallSample[];
  events: CallEventWire[];
  /** True on the batch sent as the call ends — see finish. */
  final?: boolean;
}

export interface ReporterOptions {
  /** Everything `getStats` knows, flattened. Empty when it would not answer. */
  read: () => Promise<StatEntry[]>;
  post: (batch: TelemetryBatch) => Promise<void>;
  /** How many samples may wait before a flush is due. */
  flushEvery?: number;
  /** ...and how long they may wait, whichever comes first. */
  flushAfterMs?: number;
}

/**
 * How much may wait in the buffer when nothing is getting through.
 *
 * A call on a network too broken to POST is exactly the call worth reporting,
 * so the buffer is generous — twenty minutes of samples at the default rate.
 * Past that the oldest are kept and the newest dropped: the start of a call is
 * where its setup lives, and a call this long has already said what it is.
 */
const MAX_SAMPLES = 240;
const MAX_EVENTS = 200;

const DEFAULT_FLUSH_EVERY = 6;
const DEFAULT_FLUSH_AFTER_MS = 30_000;

export class CallStatsReporter {
  private options: ReporterOptions;
  private samples: CallSample[] = [];
  private events: CallEventWire[] = [];
  private cursor: StatsCursor | null = null;
  private lastFlushAt: number | null = null;
  /** Readings and events the buffer had no room for; said out loud at the end. */
  private dropped = 0;
  /** One flush at a time: a slow POST must not be joined by the next tick. */
  private flushing = false;
  /** After `finish` this reporter is deaf — the call it belonged to is over. */
  private done = false;

  constructor(options: ReporterOptions) {
    this.options = options;
  }

  /** Something happened that is not a measurement. */
  event(kind: string, detail: string, at: number): void {
    if (this.done) return;
    if (this.events.length >= MAX_EVENTS) {
      this.dropped += 1;
      return;
    }
    this.events.push({ at, kind, detail });
  }

  /** One reading, and a flush if one is due. */
  async tick(now: number): Promise<void> {
    if (this.done) return;
    this.take(await this.read(), now);
    if (this.due(now)) await this.flush(now, false);
  }

  /**
   * The last reading and the last batch.
   *
   * The reading is taken here rather than left to the final tick because the
   * interesting part of a call is often its last five seconds — the restart
   * that did not work, the freeze it ended on — and those would otherwise be
   * lost to whatever fraction of the interval the call happened to end in.
   *
   * The caller must invoke this *before* closing the connection: `getStats` on
   * a closed peer connection answers nothing. Same rule the closing diagnostic
   * line has lived by since it existed.
   */
  async finish(now: number): Promise<void> {
    if (this.done) return;
    this.take(await this.read(), now);
    if (this.dropped > 0) {
      // Said rather than silently true: a chart with a hole in it should say
      // that the hole is a hole. Pushed past the cap on purpose — this is the
      // one line that explains every missing one.
      this.events.push({
        at: now,
        kind: 'note',
        detail: `call/telemetry/dropped · ${this.dropped} readings never got through`,
      });
    }
    this.done = true;
    await this.send({ samples: this.samples, events: this.events, final: true });
    this.samples = [];
    this.events = [];
  }

  private async read(): Promise<StatEntry[]> {
    try {
      return await this.options.read();
    } catch {
      // A connection going away refuses this, which is a sample of nulls rather
      // than a reason to stop reporting.
      return [];
    }
  }

  private take(stats: StatEntry[], now: number): void {
    const { sample, cursor } = sampleFrom(stats, this.cursor, now);
    this.cursor = cursor;
    if (this.samples.length >= MAX_SAMPLES) {
      this.dropped += 1;
      return;
    }
    this.samples.push(sample);
  }

  private due(now: number): boolean {
    if (this.samples.length === 0 && this.events.length === 0) return false;
    if (this.samples.length >= (this.options.flushEvery ?? DEFAULT_FLUSH_EVERY)) return true;
    const since = this.lastFlushAt ?? this.samples[0]?.at ?? now;
    return now - since >= (this.options.flushAfterMs ?? DEFAULT_FLUSH_AFTER_MS);
  }

  private async flush(now: number, final: boolean): Promise<void> {
    if (this.flushing) return;
    const samples = this.samples;
    const events = this.events;
    this.samples = [];
    this.events = [];
    this.flushing = true;
    const sent = await this.send({ samples, events, final });
    this.flushing = false;
    if (sent) {
      this.lastFlushAt = now;
      return;
    }
    // Back to the front of the queue, oldest first, so a retry sends the call in
    // the order it happened rather than inside out.
    this.samples = [...samples, ...this.samples].slice(0, MAX_SAMPLES);
    this.events = [...events, ...this.events].slice(0, MAX_EVENTS);
  }

  private async send(batch: TelemetryBatch): Promise<boolean> {
    if (batch.samples.length === 0 && batch.events.length === 0) return true;
    try {
      await this.options.post(batch);
      return true;
    } catch {
      return false;
    }
  }
}
